// src/AdminDashboard/components/MultiStepGameForm/GamePreview/GamePreview.js
import React from "react";
import IntroPreview from "./IntroPreview";
import GameboardPreview from "./GameboardPreview";
import CompletionPreview from "./CompletionPreview";
import clientConfig from "../../../../clientConfig";
import { resolveBackgroundImageUrl } from "../../../../Background/backgroundImageOptions";
import "./GamePreview.scss";

const GamePreview = ({ currentStep, formData, client }) => {
  if (!formData.type) {
    return (
      <div className="game-preview">
        <div className="preview-container preview-empty-state">
          Select a game type to load preview defaults.
        </div>
      </div>
    );
  }

  const normalizedClient = (client || "").toLowerCase();
  const config = clientConfig[normalizedClient] || clientConfig.default;
  const steps =
    formData.type === "memory"
      ? ["INTRO", "BRANDING", "MEMORY", "SUCCESS"]
      : ["INTRO", "BRANDING", "CONTENT", "COLORS", "SUCCESS"];
  const currentStepName = steps[currentStep - 1] || "INTRO";
  const hasGradient = Boolean(formData.makeGradient);
  const resolvedBackgroundImageUrl = resolveBackgroundImageUrl(
    formData.backgroundImage
  );
  const previewBackgroundImage = resolvedBackgroundImageUrl
    ? `url(${resolvedBackgroundImageUrl})`
    : hasGradient
    ? `linear-gradient(to bottom, #FFFFFF 0%, ${
        formData.backgroundColor || "#ffffff"
      } 100%)`
    : "none";
  // Shared dynamic styles object
  const dynamicStyles = {
    "--bg-color": formData.backgroundColor || "#ffffff",
    "--bg-image": previewBackgroundImage,
    "--text-color": formData.textColor || "#000000",
    "--button-color": formData.buttonColor || "#000000",
    "--card-bg-color": formData.cardBackgroundColor || "#ffffff",
    "--card-text-color": formData.cardTextColor || "#ffffff",
    "--redirect-button-color": formData.redirectButtonColor || "#D0343E",
    "--category-text-color": formData.categoryTextColor || "#ffffff",
  };

  const renderPreview = () => {
    switch (currentStepName) {
      case "INTRO":
      case "BRANDING":
        return (
          <IntroPreview
            formData={formData}
            dynamicStyles={dynamicStyles}
            client={client}
          />
        );
      case "CONTENT":
      case "MEMORY":
        return (
          <GameboardPreview
            formData={formData}
            dynamicStyles={dynamicStyles}
            client={client}
          />
        );
      case "COLORS":
      case "SUCCESS":
        return (
          <CompletionPreview
            formData={formData}
            dynamicStyles={dynamicStyles}
            client={client}
          />
        );
      default:
        return (
          <IntroPreview
            formData={formData}
            dynamicStyles={dynamicStyles}
            client={client}
          />
        );
    }
  };

  const getFontFormat = (fileName) => {
    const normalized = String(fileName || "").toLowerCase();
    if (normalized.endsWith(".woff2")) return "woff2";
    if (normalized.endsWith(".woff")) return "woff";
    if (normalized.endsWith(".ttf")) return "truetype";
    if (normalized.endsWith(".otf")) return "opentype";
    if (normalized.endsWith(".eot")) return "embedded-opentype";
    return "opentype";
  };

  const previewFontCSS = (() => {
    if (!config?.fonts) return "";

    if (config.fonts.type === "google") {
      const imports = (config.fonts.urls || [])
        .map((url) => `@import url("${url}");`)
        .join("\n");
      return `
        ${imports}
        .game-preview,
        .game-preview * {
          font-family: "${config.fonts.fontFamily}", "Verdana", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif !important;
        }
      `;
    }

    if (config.fonts.type === "local") {
      const fontFaceCSS = (config.fonts.files || [])
        .map(
          (f) => `
            @font-face {
              font-family: "${config.fonts.fontFamily}";
              src: url("/fonts/${f.file}") format("${getFontFormat(f.file)}");
              font-weight: ${f.weight};
              font-style: ${f.style};
              font-display: swap;
            }`
        )
        .join("\n");

      const isDelta = normalizedClient === "delta";
      const fontFamilyCSS = isDelta
        ? `
          .game-preview,
          .game-preview * {
            font-family: "Verdana", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif !important;
          }
          .game-preview h1,
          .game-preview h2,
          .game-preview h3,
          .game-preview h4,
          .game-preview h5,
          .game-preview h6,
          .game-preview button,
          .game-preview .start-button,
          .game-preview .deselect-button,
          .game-preview .submit-button,
          .game-preview .redirect-button {
            font-family: "${config.fonts.fontFamily}", "Verdana", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif !important;
          }
        `
        : `
          .game-preview,
          .game-preview * {
            font-family: "${config.fonts.fontFamily}", "Verdana", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif !important;
          }
        `;

      return `${fontFaceCSS}\n${fontFamilyCSS}`;
    }

    return "";
  })();

  return (
    <div className="game-preview">
      {previewFontCSS ? <style type="text/css">{previewFontCSS}</style> : null}
      <div className="preview-container" style={dynamicStyles}>
        {renderPreview()}
      </div>
    </div>
  );
};

export default GamePreview;
